import React, { lazy } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { PageHeader } from '../components/PageHeader';
import { StoriesSection } from '../components/StoriesSection';
import { SEO } from '../components/SEO';
import { Quote, Heart, TrendingUp } from 'lucide-react';
export function StoriesPage() {
  const testimonials = [{
    name: 'Amina Mohammed',
    role: 'Small Business Owner',
    location: 'Kano State',
    quote: 'The skills training program transformed my life. I went from struggling to feed my family to running a successful tailoring business that now employs three other women.',
    image: 'https://images.unsplash.com/photo-1531123897727-8f129e1688ce?w=400&h=400&fit=crop',
    program: 'Social Empowerment'
  }, {
    name: 'Ibrahim Adeyemi',
    role: 'Farmer',
    location: 'Ekiti State',
    quote: 'Before the agricultural training, my farm barely produced enough to survive. Now, with modern techniques and support, my yield has tripled and I can send all my children to school.',
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop',
    program: 'Agriculture & Food Security'
  }, {
    name: 'Blessing Okafor',
    role: 'Student',
    location: 'Lagos State',
    quote: 'The scholarship program gave me hope when I thought my education was over. Today, I am in university studying medicine, and I dream of returning to serve my community.',
    image: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=400&h=400&fit=crop',
    program: 'Education Support'
  }];
  return <div className="font-sans bg-warm-ivory min-h-screen w-full" data-id="element-801">
      <SEO title="Success Stories - SFRR Foundation" description="Read inspiring stories of transformation from beneficiaries of SFRR Foundation programs. Real people, real change, real hope." keywords="SFRR success stories, Nigerian community stories, transformation stories, beneficiary testimonials, impact stories Nigeria" data-id="element-802" />

      <Header data-id="element-803" />
      <main className="w-full font-sans" data-id="element-804">
        <PageHeader title="Stories of Hope & Transformation" subtitle="Real stories from the communities we serve across Nigeria" badgeText="SUCCESS STORIES" backgroundImage="https://cdn.magicpatterns.com/uploads/bJWqg8Bw6Mqe46oaFbXdse/SFRR_Foundation_(5).jpg" data-id="element-805" />

        {/* Introduction */}
        <section className="py-16 bg-white" data-id="element-806">
          <div className="container mx-auto px-4" data-id="element-807">
            <div className="max-w-3xl mx-auto text-center" data-id="element-808">
              <Quote className="w-12 h-12 text-primary mx-auto mb-6 opacity-50" data-id="element-809" />
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-charcoal mb-6" data-id="element-810">
                Every Life Has a Story
              </h2>
              <p className="text-lg text-neutral-600 leading-relaxed" data-id="element-811">
                Behind every statistic is a person. Behind every program is a
                family. These are the stories of real people whose lives have
                been transformed through the work of the SFRR Foundation.
              </p>
            </div>
          </div>
        </section>

        {/* Featured Testimonials */}
        <section className="py-20 bg-gradient-to-b from-warm-ivory to-white" data-id="element-812">
          <div className="container mx-auto px-4" data-id="element-813">
            <div className="max-w-6xl mx-auto space-y-16" data-id="element-814">
              {testimonials.map((testimonial, index) => <div key={index} className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-12 items-center animate-fadeInUp`} style={{
              animationDelay: `${index * 100}ms`
            }} data-id="element-815">
                  <div className="w-full lg:w-1/3" data-id="element-816">
                    <div className="relative" data-id="element-817">
                      <div className="absolute inset-0 bg-primary/20 rounded-3xl blur-2xl" data-id="element-818" />
                      <div className="relative aspect-square rounded-3xl overflow-hidden shadow-xl" data-id="element-819">
                        <img src={testimonial.image} alt={testimonial.name} className="w-full h-full object-cover" loading="lazy" data-id="element-820" />
                      </div>
                    </div>
                  </div>
                  <div className="w-full lg:w-2/3" data-id="element-821">
                    <span className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-semibold mb-4" data-id="element-822">
                      {testimonial.program}
                    </span>
                    <Quote className="w-10 h-10 text-primary opacity-30 mb-4" data-id="element-823" />
                    <blockquote className="text-xl md:text-2xl font-serif text-charcoal leading-relaxed mb-6" data-id="element-824">
                      "{testimonial.quote}"
                    </blockquote>
                    <div data-id="element-825">
                      <p className="font-bold text-charcoal text-lg" data-id="element-826">
                        {testimonial.name}
                      </p>
                      <p className="text-neutral-600" data-id="element-827">{testimonial.role}</p>
                      <p className="text-sm text-neutral-500" data-id="element-828">
                        {testimonial.location}
                      </p>
                    </div>
                  </div>
                </div>)}
            </div>
          </div>
        </section>

        <StoriesSection data-id="element-829" />

        {/* Impact Stats */}
        <section className="py-16 bg-white" data-id="element-830">
          <div className="container mx-auto px-4" data-id="element-831">
            <div className="max-w-4xl mx-auto" data-id="element-832">
              <div className="text-center mb-12" data-id="element-833">
                <h2 className="text-3xl font-serif font-bold text-charcoal mb-4" data-id="element-834">
                  Stories by the Numbers
                </h2>
                <p className="text-neutral-600" data-id="element-835">
                  Each number represents a life changed, a family empowered, a
                  community strengthened
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-836">
                {[{
                icon: <Heart className="w-8 h-8" data-id="element-837" />,
                number: '500+',
                label: 'Stories Shared',
                color: 'bg-accent'
              }, {
                icon: <TrendingUp className="w-8 h-8" data-id="element-838" />,
                number: '85%',
                label: 'Success Rate',
                color: 'bg-primary'
              }, {
                icon: <Quote className="w-8 h-8" data-id="element-839" />,
                number: '50+',
                label: 'Communities',
                color: 'bg-secondary'
              }].map((stat, index) => <div key={index} className="bg-warm-ivory rounded-2xl p-8 text-center shadow-soft" data-id="element-840">
                    <div className={`w-16 h-16 rounded-xl ${stat.color} bg-opacity-10 flex items-center justify-center mx-auto mb-4`} data-id="element-841">
                      <div className={`${stat.color.replace('bg-', 'text-')}`} data-id="element-842">
                        {stat.icon}
                      </div>
                    </div>
                    <div className="text-4xl font-bold text-charcoal mb-2" data-id="element-843">
                      {stat.number}
                    </div>
                    <div className="text-neutral-600" data-id="element-844">{stat.label}</div>
                  </div>)}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-gradient-to-br from-primary/5 to-secondary/5" data-id="element-845">
          <div className="container mx-auto px-4" data-id="element-846">
            <div className="max-w-3xl mx-auto text-center" data-id="element-847">
              <h2 className="text-3xl font-serif font-bold text-charcoal mb-6" data-id="element-848">
                Be Part of Someone's Story
              </h2>
              <p className="text-lg text-neutral-600 mb-8" data-id="element-849">
                Your support can help write the next chapter of hope and
                transformation
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-850">
                <a href="/donate" className="px-8 py-4 bg-primary text-white rounded-full font-semibold hover:bg-primary-600 transition-all duration-300 hover:-translate-y-0.5 shadow-md hover:shadow-lg" data-id="element-851">
                  Support Our Work
                </a>
                <a href="/get-involved" className="px-8 py-4 bg-white text-charcoal border-2 border-neutral-200 rounded-full font-semibold hover:border-primary hover:text-primary transition-all duration-300" data-id="element-852">
                  Get Involved
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer data-id="element-853" />
    </div>;
}